<script setup lang="ts">
import { useConfirm } from "primevue/useconfirm";
import { computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps({
    type: {
        type: String,
        required: true,
        default: "profile",
        validator: function (value: string) {
            return ["profile", "group-chart"].includes(value);
        },
    },
    participantId: {
        type: String,
        required: true,
    },
    name: {
        type: String,
        required: true,
    },
    outlined: {
        type: Boolean,
        default: false,
        required: false,
    },
});

const { t } = useI18n();

const emit = defineEmits(["open:profile", "close:dialog"]);

const confirm = useConfirm();

const text = computed(() => {
    if (props.type === "profile") {
        return {
            header: t("project-details.participants.actionsLabels.view-profile"),
            message: t("project-details.participants.actionsLabels.view-profile-message"),
        };
    } else {
        return {
            header: t("project-details.group-chart.view-group-chart"),
            message: t("project-details.group-chart.view-group-chart-message"),
        };
    }
});

const showConfirm = () => {
    confirm.require({
        group: props.name + props.participantId,
        message: text.value.message,
        header: text.value.header,
        accept: () => {
            emit("open:profile");
        },
        reject: () => {
            emit("close:dialog");
        },
    });
};

onMounted(() => {
    showConfirm();
});
</script>

<template>
    <ConfirmDialog
        :group="name + participantId"
        :dismissable-mask="false"
        :block-scroll="true"
        :draggable="false"
        :id="`profile-view-confirmation-dialog_confirm-dialog-${participantId}`"
    >
        <template #container="{ message: dialogMessage, acceptCallback, rejectCallback }">
            <div class="flex flex-col items-center p-8 bg-surface rounded">
                <div
                    class="rounded-full bg-brand text-brand-contrast inline-flex justify-center items-center h-24 w-24 -mt-20"
                >
                    <i class="pi pi-question text-5xl text-white"></i>
                </div>
                <span class="font-bold text-2xl block mb-2 mt-6">{{ dialogMessage.header }}</span>
                <p class="mb-0">{{ dialogMessage.message }}</p>
                <div class="flex items-center gap-2 mt-6">
                    <Button
                        :id="`profile-view-confirmation_btn-confirm-${participantId}`"
                        :label="text.header"
                        @click="acceptCallback"
                    ></Button>
                    <Button :label="t('buttons.cancel')" outlined @click="rejectCallback"></Button>
                </div>
            </div>
        </template>
    </ConfirmDialog>
</template>
